import { createContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../services/apiClient';

export interface FeatureFlags {
  clinics: boolean;
  coverage: boolean;
  procedures: boolean;
  tasks: boolean;
  admin: boolean;
  adminCutover: boolean;
  dashboard: boolean;
  settings: boolean;
  security: boolean;
  fees: boolean;
  contacts: boolean;
  customers: boolean;
  clinicClientAccounts: boolean;
  clinicHierarchy: boolean;
  clinicHierarchyAttributes: boolean;
  /**
   * "Management" page (CustomerLocationBrowserPage). Off by default during
   * MVP — Hank, 2026-04-30: not ready, hide for now. Flip on in App Config
   * (`ClinicMgmt.CustomerLocationBrowser.Enabled = True`) when shippable.
   */
  customerLocationBrowser: boolean;
  /**
   * "Client Coverage" page (ClientCoveragePage + LocationCoverageDetailPage).
   * Off by default during MVP — Hank, 2026-04-30: not ready, hide for now.
   * Flip on in App Config (`ClinicMgmt.ClientCoverage.Enabled = True`).
   */
  clientCoverage: boolean;
}

const defaultFlags: FeatureFlags = {
  clinics: true,
  coverage: true,
  procedures: true,
  tasks: true,
  admin: true,
  adminCutover: true,
  dashboard: true,
  settings: true,
  security: true,
  fees: true,
  contacts: true,
  customers: true,
  clinicClientAccounts: true,
  clinicHierarchy: true,
  clinicHierarchyAttributes: true,
  customerLocationBrowser: false,
  clientCoverage: false,
};

const flagMapping: Record<string, keyof FeatureFlags> = {
  'ClinicMgmt.Clinics.Enabled': 'clinics',
  'ClinicMgmt.Coverage.Enabled': 'coverage',
  'ClinicMgmt.Procedures.Enabled': 'procedures',
  'ClinicMgmt.TaskRequests.Enabled': 'tasks',
  'ClinicMgmt.Admin.Enabled': 'admin',
  'ClinicMgmt.Admin.Cutover.Enabled': 'adminCutover',
  'ClinicMgmt.Dashboard.Enabled': 'dashboard',
  'ClinicMgmt.Security.Enabled': 'security',
  'ClinicMgmt.Fees.Enabled': 'fees',
  'ClinicMgmt.Contacts.Enabled': 'contacts',
  'ClinicMgmt.Customers.Enabled': 'customers',
  'ClinicMgmt.ClinicClientAccounts.Enabled': 'clinicClientAccounts',
  'ClinicMgmt.ClinicHierarchy.Enabled': 'clinicHierarchy',
  'ClinicMgmt.ClinicHierarchyAttributes.Enabled': 'clinicHierarchyAttributes',
  'ClinicMgmt.CustomerLocationBrowser.Enabled': 'customerLocationBrowser',
  'ClinicMgmt.ClientCoverage.Enabled': 'clientCoverage',
};

// eslint-disable-next-line react-refresh/only-export-components
export const FeatureFlagContext = createContext<FeatureFlags>(defaultFlags);

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const { data: flags } = useQuery({
    queryKey: ['feature-flags'],
    queryFn: async (): Promise<FeatureFlags> => {
      const response = await apiClient.get<{ data: Record<string, boolean> }>('/config/features');
      const apiFlags = response.data.data;

      const mapped: FeatureFlags = { ...defaultFlags };
      for (const [apiKey, uiKey] of Object.entries(flagMapping)) {
        if (apiKey in apiFlags) {
          mapped[uiKey] = apiFlags[apiKey];
        }
      }
      // settings is always true
      mapped.settings = true;
      return mapped;
    },
    staleTime: Infinity,
  });

  return (
    <FeatureFlagContext.Provider value={flags ?? defaultFlags}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

